/**
 * 统一的 Modal 弹窗样式配置
 *
 * @description 提供一致的弹窗样式，符合 Emerald 主题设计规范
 * @version 1.0.0
 * @date 2025-01-24
 */

import React from 'react';
import type { ModalProps } from 'antd';

/**
 * Emerald 主题色配置
 */
export const emeraldColors = {
  primary: '#10b981', // emerald-500
  primaryDark: '#059669', // emerald-600
  primaryLight: '#34d399', // emerald-400
  success: '#10b981',
  error: '#ef4444',
  errorDark: '#dc2626',
  warning: '#f59e0b',
  info: '#3b82f6',
  border: 'rgba(16, 185, 129, 0.1)',
  borderStrong: 'rgba(16, 185, 129, 0.3)',
  background: 'rgba(255, 255, 255, 0.9)',
  backgroundGradient:
    'linear-gradient(135deg, #f8fffe 0%, #f0fdf4 50%, rgba(236, 253, 245, 0.3) 100%)',
  shadow: 'rgba(16, 185, 129, 0.08)',
  shadowStrong: 'rgba(16, 185, 129, 0.15)',
};

/**
 * 统一的 Modal 基础样式配置
 */
export const getUnifiedModalStyles = (): ModalProps['styles'] => ({
  mask: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
  },
  body: {
    background: emeraldColors.backgroundGradient,
    padding: '24px',
  },
  header: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    borderRadius: '8px 8px 0 0',
    marginBottom: 0,
    padding: '16px 24px',
  },
  footer: {
    background:
      'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.02) 100%)',
    borderTop: `1px solid ${emeraldColors.border}`,
    padding: '16px 24px',
    marginTop: 0,
  },
});

/**
 * 统一的 Modal 通用配置
 */
export const getUnifiedModalProps = (): Partial<ModalProps> => ({
  centered: true,
  styles: getUnifiedModalStyles(),
  maskClosable: false,
  destroyOnClose: true,
});

/**
 * 获取统一的 Modal 标题样式
 */
export const getUnifiedModalTitle = (
  icon: React.ReactNode,
  title: string,
  style?: React.CSSProperties
): React.ReactNode => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: 'white',
      fontSize: '16px',
      fontWeight: 600,
      ...style,
    }}
  >
    {icon && <span style={{ fontSize: '18px' }}>{icon}</span>}
    <span>{title}</span>
  </div>
);

/**
 * 获取卡片样式（用于弹窗内部的卡片）
 */
export const getCardStyle = (
  customStyle?: React.CSSProperties
): React.CSSProperties => ({
  marginBottom: 20,
  background: emeraldColors.background,
  backdropFilter: 'blur(20px)',
  border: `1px solid ${emeraldColors.border}`,
  borderRadius: '16px',
  boxShadow: `0 8px 32px ${emeraldColors.shadow}`,
  transition: 'all 0.3s ease',
  ...customStyle,
});

/**
 * 获取卡片标题样式
 */
export const getCardTitleStyle = (
  icon: React.ReactNode,
  title: string
): React.ReactNode => (
  <div
    style={{
      background:
        'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
      margin: '-12px -16px 12px -16px',
      padding: '12px 16px',
      borderRadius: '12px 12px 0 0',
      borderBottom: `1px solid ${emeraldColors.border}`,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ color: emeraldColors.primary, fontSize: '16px' }}>
        {icon}
      </span>
      <span style={{ color: '#065f46', fontWeight: 600, fontSize: '15px' }}>
        {title}
      </span>
    </div>
  </div>
);

/**
 * 获取主要按钮样式
 */
export const getPrimaryButtonStyle = (
  type: 'primary' | 'success' | 'danger' = 'primary'
): React.CSSProperties => {
  const gradientMap = {
    primary: `linear-gradient(135deg, ${emeraldColors.primary} 0%, ${emeraldColors.primaryDark} 100%)`,
    success: `linear-gradient(135deg, ${emeraldColors.success} 0%, ${emeraldColors.primaryDark} 100%)`,
    danger: `linear-gradient(135deg, ${emeraldColors.error} 0%, ${emeraldColors.errorDark} 100%)`,
  };

  const shadowMap = {
    primary: `0 2px 8px ${emeraldColors.shadowStrong}`,
    success: `0 2px 8px ${emeraldColors.shadowStrong}`,
    danger: '0 2px 8px rgba(239, 68, 68, 0.3)',
  };

  return {
    background: gradientMap[type],
    borderColor:
      type === 'danger' ? emeraldColors.error : emeraldColors.primary,
    color: '#ffffff',
    boxShadow: shadowMap[type],
    fontWeight: 500,
    borderRadius: '6px',
    height: '36px',
    minWidth: '80px',
  };
};

/**
 * 获取次要按钮样式
 */
export const getSecondaryButtonStyle = (): React.CSSProperties => ({
  borderColor: '#d1d5db',
  color: '#6b7280',
  borderRadius: '6px',
  height: '36px',
  minWidth: '80px',
  fontWeight: 500,
});

/**
 * 获取取消按钮样式（带边框的主题色按钮）
 */
export const getCancelButtonStyle = (): React.CSSProperties => ({
  borderColor: emeraldColors.primary,
  color: emeraldColors.primary,
  borderRadius: '6px',
  height: '36px',
  minWidth: '80px',
  fontWeight: 500,
});

/**
 * 大尺寸弹窗配置（适用于详情页等）
 */
export const getLargeModalProps = (): Partial<ModalProps> => ({
  ...getUnifiedModalProps(),
  width: 1200,
  style: { top: 20 }, // 固定 top 值而不是百分比，避免小屏幕显示问题
});

/**
 * 中等尺寸弹窗配置（适用于表单等）
 */
export const getMediumModalProps = (): Partial<ModalProps> => ({
  ...getUnifiedModalProps(),
  width: 900,
});

/**
 * 小尺寸弹窗配置（适用于确认框等）
 */
export const getSmallModalProps = (): Partial<ModalProps> => ({
  ...getUnifiedModalProps(),
  width: 600,
});

/**
 * 获取内容区域滚动样式
 */
export const getScrollableContentStyle = (
  maxHeight = '70vh'
): React.CSSProperties => ({
  maxHeight,
  overflowY: 'auto',
  padding: '0 24px',
});

/**
 * 获取警告提示框样式
 */
export const getAlertStyle = (
  type: 'success' | 'error' | 'warning' | 'info' = 'info'
): React.CSSProperties => {
  const colorMap = {
    success: {
      background: 'rgba(16, 185, 129, 0.1)',
      border: `1px solid ${emeraldColors.primary}`,
    },
    error: {
      background: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid #ef4444',
    },
    warning: {
      background: 'rgba(245, 158, 11, 0.1)',
      border: '1px solid #f59e0b',
    },
    info: {
      background: 'rgba(59, 130, 246, 0.1)',
      border: '1px solid #3b82f6',
    },
  };

  return {
    marginBottom: '16px',
    borderRadius: '8px',
    ...colorMap[type],
  };
};

/**
 * 获取渐变背景文本框样式（用于重要内容展示）
 */
export const getHighlightBoxStyle = (
  type: 'success' | 'error' | 'warning' = 'success'
): React.CSSProperties => {
  const styleMap = {
    success: {
      background:
        'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.08) 100%)',
      border: `2px solid ${emeraldColors.borderStrong}`,
      boxShadow: `0 8px 32px ${emeraldColors.shadowStrong}`,
    },
    error: {
      background:
        'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.08) 100%)',
      border: '2px solid rgba(239, 68, 68, 0.3)',
      boxShadow: '0 8px 32px rgba(239, 68, 68, 0.15)',
    },
    warning: {
      background:
        'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.08) 100%)',
      border: '2px solid rgba(245, 158, 11, 0.3)',
      boxShadow: '0 8px 32px rgba(245, 158, 11, 0.15)',
    },
  };

  return {
    padding: '12px 16px',
    borderRadius: '12px',
    marginBottom: '16px',
    ...styleMap[type],
  };
};

/**
 * 获取 Footer 底部操作栏样式
 */
export const getFooterStyle = (): React.CSSProperties => ({
  background:
    'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(5, 150, 105, 0.02) 100%)',
  padding: '16px 24px',
  margin: '16px -24px -16px -24px',
  borderTop: `1px solid ${emeraldColors.border}`,
});
